import React from 'react';
import { T, gradeFor, gradeInkFor, UNGRADED } from '../designTokens';


interface ScoreRingProps {
  /** null for a scan that assessed nothing (see isUngraded): an empty N/A ring, never an F. */
  score: number | null;
  size?: number;
  animated?: boolean;
}

export default function ScoreRing({ score, size = 180, animated = true }: ScoreRingProps) {
  const stroke = Math.max(6, Math.round(size * 0.056)); // ~10px at 180
  const r = size / 2;
  const normalizedRadius = r - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const progress = score === null ? 0 : (score / 100) * circumference;
  const { letter: grade, color } = score === null ? UNGRADED : gradeFor(score);
  // The arc takes the grade hue; the letter is text, so it takes the AA ink tone.
  const letterColor = score === null ? UNGRADED.color : gradeInkFor(score);
  // Under reduced motion the ring renders its final state with no sweep.
  const reduceMotion = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Scale font sizes proportionally
  const gradeFontSize = Math.round(size * 0.289); // ~52px at 180
  // Floored at 12px: at the teaser's ~100px ring the proportional size fell
  // to 7px, which left the actual score unreadable.
  const scoreFontSize = Math.max(12, Math.round(size * 0.072)); // ~13px at 180
  const gradeY = r * 0.91;   // ~82 at 180
  const scoreY = r * 1.2;    // ~108 at 180

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={r}
          cy={r}
          r={normalizedRadius}
          fill="none"
          stroke={T.gray200}
          strokeWidth={stroke}
        />
        <circle
          cx={r}
          cy={r}
          r={normalizedRadius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={`${progress} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${r} ${r})`}
          style={animated && !reduceMotion ? { transition: 'stroke-dasharray 0.8s cubic-bezier(0.16, 1, 0.3, 1)' } : undefined}
        />
        <text
          x={r}
          y={gradeY}
          textAnchor="middle"
          fontSize={gradeFontSize}
          fontWeight={700}
          fontFamily={T.fontMono}
          fill={letterColor}
        >
          {grade}
        </text>
        <text
          x={r}
          y={scoreY}
          textAnchor="middle"
          fontSize={scoreFontSize}
          fontFamily={T.fontMono}
          fill={T.gray600}
        >
          {score === null ? 'Not scored' : `${score}/100`}
        </text>
      </svg>
    </div>
  );
}
